import * as nodePath from 'node:path';
import { isParsableJson } from '@ai-sdk/provider-utils';
import { atom, type PreinitializedWritableAtom } from 'nanostores';
import { parse as parseToml, stringify as stringifyToml, TomlError } from 'smol-toml';
import { buildOnNetlify, deployOnNetlify } from '~/lib/.client/api/netlify';
import { getOrCreateSite } from '~/lib/.client/api/sites';
import { fetchStreamedText } from '~/lib/.client/fetch';
import { watchBuildLogsFromNetlify } from '~/lib/.client/netlify-logs';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { ChatError, FixDiffError, FixLazyError } from '~/lib/errors';
import { NETLIFY_TOML, PACKAGE_JSON, PACKAGE_LOCK, type Framework } from '~/lib/persistence/frameworks';
import { checkForUpgrades } from '~/lib/persistence/npm-packages';
import { importStore, waitForProjectImport, waitForProjectImportFiles } from '~/lib/projects';
import { importModifications } from '~/lib/projects/importStore';
import { resolveBuildConfig } from '~/lib/runtime/build-config';
import { CTRL_C } from '~/lib/shell/constants';
import type { ShellId } from '~/lib/shell/driver';
import { analysisWatcher } from '~/lib/static-analysis/watcher';
import { applicationManager } from '~/lib/stores/applicationManager';
import { descriptionStore } from '~/lib/stores/description';
import { waitForFigmaFetch, waitForFigmaGenerate } from '~/lib/stores/figma';
import { githubSyncs } from '~/lib/stores/github';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore, type SupabaseStatus } from '~/lib/stores/supabase';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import type { GetOrCreateSiteResponse } from '~/routes/api.deploy.$project.($id)';
import type { BuildConfig } from '~/templates';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { WORK_DIR } from '~/utils/constants';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';
import { isAtom } from '~/utils/nanostore';
import { removeTrailingSlashes } from '~/utils/path';
import type { PromiseWithQueue } from '~/utils/promises';
import { promiseQueue, wait } from '~/utils/promises';
import { coloredText, createSpinner } from '~/utils/terminal';
import { unreachable } from '~/utils/unreachable';
import { addWorkDir } from '~/utils/workDir';

type UnwrapAtom<T> = {
  [K in keyof T]: T[K] extends PreinitializedWritableAtom<infer U> ? U : T[K];
};

// we intentionally don't include 'supabase' here because it's not a valid type in a <boltAction> tag
export const actionTypes = new Set([
  'file',
  'shell',
  'import-template',
  'import-project',
  'import',
  'deploy',
  'build',
  'build-and-deploy',
  'domain-setup',
  'install',
  'start',
  'github-sync',
] as ActionType[]);

export type ActionType = BoltAction['type'];

export type ActionStatus = 'pending' | 'running' | 'complete' | 'aborted' | 'failed' | 'skipped';

export type ActionUpdate = Partial<UnwrapAtom<Pick<BoltAction, 'status' | 'executed' | 'content'>>>;

/**
 * These are uppercase unlike other types because the action type is used in the UI
 * and it's a bit wasteful to construct a new string when the action gets rendered.
 */
export type FileActionType = 'Create' | 'Update';

export type SupabaseActionType =
  | 'oauth'
  | 'create-project'
  | 'setup-auth'
  | 'create-database'
  | 'edge-functions'
  | 'finalize'
  | 'existing-database';

abstract class BaseAction {
  abstract readonly type: unknown;

  executed = false;

  input?: unknown;
  error?: string;

  status = atom<ActionStatus>('pending');
  content = atom('');
  actionDetails = atom<string | undefined>();

  #abortController = new AbortController();

  listen(cb: VoidFunction) {
    const disposers = [this.status.listen(cb), this.content.listen(cb)];

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }

  get abortSignal() {
    return this.#abortController.signal;
  }

  constructor(
    readonly artifactId: string,
    readonly messageId: string,
    readonly id: string,
    content = '',
  ) {
    this.content.set(content);
  }

  update(updates: ActionUpdate) {
    for (const [_key, value] of Object.entries(updates)) {
      const key = _key as keyof this;

      if (value != null) {
        if (isAtom(this[key])) {
          if (key === 'status' && this[key].get() !== 'aborted') {
            this[key].set(value as any);
          }
        } else {
          (this as any)[key] = value;
        }
      }
    }
  }

  abort() {
    if (this.status.value === 'aborted') {
      return;
    }

    this.#abortController.abort('aborted');
    this.status.set('aborted');
  }
}

export abstract class PipeAction extends BaseAction {
  actions = atom<BoltAction[]>([]);

  constructor(artifactId: string, messageId: string, id: string, actions: BoltAction[]) {
    super(artifactId, messageId, id);

    this.actions.set(actions);
  }

  update(updates: ActionUpdate): void {
    super.update(updates);

    if ('status' in updates && updates.status === 'complete') {
      for (const action of this.actions.get()) {
        action.update({ status: updates.status });
      }
    }
  }

  listen(cb: VoidFunction) {
    const disposers = [this.actions.listen(cb), super.listen(cb)];

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }

  async run() {
    let output: any = undefined;

    let currentAction: BoltAction;

    this.abortSignal.addEventListener('abort', () => {
      currentAction.abort();
    });

    for (currentAction of this.actions.get()) {
      if (this.abortSignal.aborted) {
        // do not run any other actions if the action was aborted
        break;
      }

      if (currentAction.status.get() !== 'pending') {
        continue;
      }

      try {
        currentAction.input = output;

        currentAction.status.set('running');

        output = await currentAction.run();

        if (currentAction.status.value === 'running' || currentAction.status.value === 'complete') {
          currentAction.status.set('complete');
        } else if (currentAction.status.value === 'skipped') {
          currentAction.status.set('skipped');
        } else {
          this.status.set(currentAction.status.value);
          break;
        }
      } catch (error) {
        currentAction.status.set('failed');

        throw error;
      }
    }

    return output;
  }
}

export type FileActionContentType = 'diff' | 'content';

export class FileAction extends BaseAction {
  readonly type = 'file';
  readonly actionType: FileActionType;

  #fixedContent?: PromiseWithQueue<string>;

  get filePath() {
    const filePath = this.actionDetails.get();

    invariant(filePath);

    return filePath;
  }

  constructor(
    artifactId: string,
    messageId: string,
    id: string,
    filePath: string,
    runAction: boolean,
    public contentType?: FileActionContentType,
    content?: string,
  ) {
    super(artifactId, messageId, id, content);

    const files = workbenchStore.files.get();

    // if the AI generated a SQL file we modify the path but only if the action is executed
    if (filePath.endsWith('.sql') && runAction) {
      filePath = supabaseStore.getMigrationFilePath(filePath);
    }

    this.actionDetails.set(filePath);

    this.actionType = files[addWorkDir(filePath)] ? 'Update' : 'Create';
  }

  async run() {
    let content = this.content.value;

    if (this.#fixedContent) {
      const results = await this.#fixedContent.promise;
      content = results.at(-1)!;
    }

    const fullPath = addWorkDir(removeTrailingSlashes(this.filePath));

    /**
     * WebContainer must be loaded before we process file actions.
     *
     * Specifically, we cannot queue anything in the analysis watcher before we are able to observe the result in `watchPaths`.
     *
     * This also relys on the implicit behaviour that we `await webcontainer` in the AnalysisWatcher before we do it here.
     */
    await webcontainerPromise;

    analysisWatcher.queueAdd(fullPath);

    try {
      await workbenchStore.writeFile(this.filePath, {
        type: 'file',
        content,
        isBinary: false,
        isStackBlitzBinary: false,
      });
    } catch (error) {
      analysisWatcher.queueRemove(fullPath);

      throw error;
    }
  }

  fixLaziness(
    currentFileContent: string,
    originalContent: string,
    onFileStream: (content: string, done: boolean) => void,
  ) {
    if (import.meta.env.DEV) {
      logger.warn(`Fixing laziness detected in file ${this.filePath}`);
    }

    const fixer = async () => {
      const newFilecontent = await fetchStreamedText('/api/lazy/fix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileContent: originalContent,
          newFileContent: currentFileContent,
          messageId: this.messageId,
        }),
        silenceError: true,
        errorParser: FixLazyError,
        onTextStream: (content) => onFileStream(content, false),
      });

      const finalContent = newFilecontent ?? currentFileContent;

      onFileStream(finalContent, true);

      return finalContent;
    };

    if (!this.#fixedContent) {
      this.#fixedContent = promiseQueue();
    }

    this.#fixedContent.enqueue(fixer());
  }

  fixDiff(
    currentFileContent: string,
    originalContent: string,
    diff: string,
    onFileStream: (content: string, done: boolean) => void,
  ) {
    if (import.meta.env.DEV) {
      logger.warn(`Fixing file ${this.filePath}`);
    }

    const fixer = async () => {
      const newFilecontent = await fetchStreamedText('/api/diffs/fix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileContent: originalContent,
          diff,
          messageId: this.messageId,
        }),
        silenceError: true,
        errorParser: FixDiffError,
        onTextStream: (content) => onFileStream(content, false),
      });

      const finalContent = newFilecontent ?? currentFileContent;

      onFileStream(finalContent, true);

      return finalContent;
    };

    if (!this.#fixedContent) {
      this.#fixedContent = promiseQueue();
    }

    this.#fixedContent.enqueue(fixer());
  }
}

export class ImportAction extends PipeAction {
  readonly type = 'import';

  #cloning = false;

  constructor(
    artifactId: string,
    messageId: string,
    id: string,
    public target: string,
    kind: string | undefined,
    cloning: string | undefined,
  ) {
    const nestedActions: BoltAction[] =
      kind === 'figma'
        ? [
            new FetchFigmaFrameAction(artifactId, messageId, `${id}-fetch`, target),
            new GenerateFigmaCodeAction(artifactId, messageId, `${id}-generate`),
            new UploadFigmaAssetsAction(artifactId, messageId, `${id}-upload`),
          ]
        : [new ImportProjectAction(artifactId, messageId, `${id}-import`, target)];

    super(artifactId, messageId, id, nestedActions);

    this.#cloning = cloning === 'true';
  }

  async run() {
    await super.run();

    const files = workbenchStore.files.get();

    if (!files) {
      return;
    }

    const packageJsonFile = files[addWorkDir(PACKAGE_JSON)];
    const packageJsonContents = packageJsonFile?.type === 'file' && packageJsonFile.content;

    const framework = workbenchStore.framework.get();

    const actions: BoltAction[] = [];

    if (this.#cloning) {
      actions.push(new GithubCloneAction(this.artifactId, this.messageId, `${this.id}-clone`));
    }

    /**
     * This logic is similar to the `addAction` and `updateArtifact` functions in `workbench.ts`
     * because project imports are async.
     *
     * While `addAction` and `updateArtifact` handle install/start actions for new actions being added,
     * this code ensures these actions are added after an import is fully complete.
     *
     * This is necessary because `addAction` may be called before the import finishes when loading
     * an existing project.
     */

    if (packageJsonContents) {
      actions.push(new InstallAction(this.artifactId, this.messageId, `${this.id}-install`));
    }

    if (framework) {
      actions.push(new StartAction(this.artifactId, this.messageId, `${this.id}-start`, false, framework));
    }

    this.actions.set([...this.actions.get(), ...actions]);

    this.update({ status: 'running' });

    await super.run();
  }
}

export class ImportProjectAction extends BaseAction {
  readonly type = 'import-project';

  constructor(artifactId: string, messageId: string, id: string, target: string, content?: string) {
    super(artifactId, messageId, id, content);

    this.actionDetails.set(target);
  }

  async run() {
    await waitForProjectImportFiles();
  }
}

export class GithubSyncAction extends BaseAction {
  readonly type = 'github-sync';

  #oid: string;

  constructor(artifactId: string, messageId: string, id: string, oid: string) {
    super(artifactId, messageId, id);

    this.#oid = oid;
  }

  async run() {
    const item = await githubSyncs.next();

    if (item.done || item.value.oid !== this.#oid) {
      if (import.meta.env.DEV) {
        unreachable('unexpected');
      }

      return;
    }

    await item.value.task;
  }
}

export class GithubCloneAction extends BaseAction {
  readonly type = 'github-clone';

  constructor(artifactId: string, messageId: string, id: string) {
    super(artifactId, messageId, id);
  }

  async run() {
    await importModifications.promise;
  }
}

export class FetchFigmaFrameAction extends BaseAction {
  readonly type = 'fetch-figma-frame';

  constructor(artifactId: string, messageId: string, id: string, target: string, content?: string) {
    super(artifactId, messageId, id, content);

    this.actionDetails.set(target);
  }

  async run() {
    await waitForFigmaFetch();
  }
}

export class GenerateFigmaCodeAction extends BaseAction {
  readonly type = 'generate-figma-code';

  constructor(artifactId: string, messageId: string, id: string) {
    super(artifactId, messageId, id);
  }

  async run() {
    await waitForFigmaGenerate();
  }
}

export class UploadFigmaAssetsAction extends BaseAction {
  readonly type = 'upload-figma-assets';

  constructor(artifactId: string, messageId: string, id: string) {
    super(artifactId, messageId, id);
  }

  async run() {
    await waitForProjectImport();
  }
}

export class SupabaseAction extends BaseAction {
  readonly type = 'supabase';

  constructor(
    artifactId: string,
    messageId: string,
    id: string,
    public actionType: SupabaseActionType,
  ) {
    super(artifactId, messageId, id);
  }

  async run() {
    let unsubscribe: VoidFunction | undefined;

    try {
      await new Promise<void>((resolve, reject) => {
        switch (this.actionType) {
          case 'oauth': {
            applicationManager.loadApplicationsConfiguration().then(() => {
              unsubscribe = applicationManager.applications.subscribe((applications, prevApplications) => {
                const currentAuthStatus = applications.SUPABASE;

                if (prevApplications && currentAuthStatus === 'DISCONNECTED') {
                  reject();
                } else if (applications.SUPABASE === 'CONNECTED') {
                  resolve();
                }
              });
            });

            break;
          }
          case 'create-project': {
            this.waitAndListenForStatusUpdates(resolve, reject);

            break;
          }
          case 'setup-auth': {
            this.waitAndListenForStatusUpdates(resolve, reject);

            break;
          }
          case 'create-database': {
            this.waitAndListenForStatusUpdates(resolve, reject);

            break;
          }
          case 'edge-functions': {
            supabaseStore.deployEdgeFunctions().then(resolve, reject);

            break;
          }
          case 'finalize':
          case 'existing-database': {
            this.listenForProjectStatus('CONFIGURED').then(resolve, reject);

            break;
          }
          default: {
            logger.warn('Unknown action type');
          }
        }
      });
    } finally {
      unsubscribe?.();
    }
  }

  waitAndListenForStatusUpdates(success: () => void, failure: (f: any) => void) {
    // random number between 2000 and 3500
    const timeout = (Math.floor(Math.random() * 1.5) + 2) * 1000;

    Promise.race([wait(timeout), this.listenForProjectStatus('CONFIGURED')])
      .then(() => success())
      .catch(() =>
        failure({
          code: 'unknown',
          message: 'Could not connect to Supabase.',
          isRetryable: false,
        }),
      );
  }

  listenForProjectStatus(status: SupabaseStatus) {
    return new Promise<void>((resolve, reject) => {
      const unsubscribe = supabaseStore.supabaseStatus.subscribe((updatedStatus) => {
        if (updatedStatus === 'NOT_CONFIGURED') {
          // something went wrong when setting up the project
          reject();

          queueMicrotask(() => unsubscribe());
        } else if (updatedStatus === status) {
          resolve();

          queueMicrotask(() => unsubscribe());
        }
      });
    });
  }
}

export abstract class BaseShellAction extends BaseAction {
  #isDone = false;
  #command = '';

  shellId: ShellId = 'bolt';

  exitCode: number | null = null;
  output = '';
  stderr = '';

  constructor(artifactId: string, messageId: string, id: string, content?: string) {
    super(artifactId, messageId, id, content);
  }

  get command() {
    return this.#command || this.content.get();
  }

  set command(command: string) {
    this.#command = command;
  }

  async execute(this: Extract<BoltAction, { command: string }>) {
    const process = workbenchStore.runShellAction(this);

    this.abortSignal.addEventListener('abort', () => {
      if (!this.#isDone) {
        process.kill();
      }
    });

    try {
      this.exitCode = await process.exit;

      logger.debug(`Process terminated with code ${this.exitCode}`);

      let status: ActionStatus = 'complete';

      if (this.exitCode === 130) {
        // CTRL-C aborted by driver
        status = 'aborted';
      } else if (this.exitCode !== 0) {
        status = 'failed';
      }

      this.status.set(status);

      return this.exitCode;
    } catch {
      // if process.exit throws, this means it was skipped and we return `undefined`
      this.status.set('skipped');

      return undefined;
    } finally {
      process.done();
      this.#isDone = true;
    }
  }
}

export class InstallAction extends BaseShellAction {
  static isSameAsInstallAction(action: ShellAction) {
    return action.command.trim() === 'npm install' || action.command.trim().startsWith('npm add ');
  }

  readonly type = 'install';

  #timer: ReturnType<typeof setTimeout> | undefined;
  #peerDeps = false;

  constructor(artifactId: string, messageId: string, id: string, peerDeps = false, content: string = 'npm install') {
    super(artifactId, messageId, id, peerDeps ? `${content} --legacy-peer-deps` : content);
    this.#peerDeps = peerDeps;

    this.#timer = setTimeout(() => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.NpmInstallNeverRan,
        },
      });
    }, 60_000);
  }

  async run() {
    clearTimeout(this.#timer);

    const files = workbenchStore.files.get();

    const packageJsonFile = files[addWorkDir(PACKAGE_JSON)];
    const packageJsonContent = packageJsonFile?.type === 'file' && packageJsonFile.content;
    const packageLockFile = files[addWorkDir(PACKAGE_LOCK)];
    const packageLockContent = packageLockFile?.type === 'file' && packageLockFile.content;

    if (packageJsonContent && packageLockContent) {
      const upgrades = checkForUpgrades(packageJsonContent, packageLockContent);

      if (upgrades.length > 0) {
        this.content.set(`npm upgrade ${upgrades.join(' ')} && ${this.content.get()}`);
      }
    }

    const sendFailureEvent = (properties: { exitCode?: number; output: string; peerDeps: boolean }) => {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.NpmInstallFailed,
          properties,
        },
      });
    };

    let exitCode = await this.execute();

    if (exitCode === 0) {
      return 0;
    }

    const canRetry = !this.#peerDeps;

    if (canRetry) {
      sendFailureEvent({ exitCode, output: this.output, peerDeps: this.#peerDeps });
    }

    const retry = this.output.includes('ERESOLVE') && canRetry;

    if (!retry) {
      return exitCode;
    }

    this.status.set('running');

    const subAction = this.#withPeerDeps();

    exitCode = await subAction.execute();

    if (exitCode !== 0) {
      sendFailureEvent({ exitCode, output: subAction.output, peerDeps: subAction.#peerDeps });
    }

    this.status.set(subAction.status.get());

    return exitCode;
  }

  #withPeerDeps() {
    return new InstallAction(this.artifactId, this.messageId, `install-with-peer-deps:${this.messageId}`, true);
  }
}

export class StartAction extends BaseShellAction {
  readonly type = 'start';

  constructor(
    artifactId: string,
    messageId: string,
    id: string,

    public autocreated: boolean,
    contentOrFramework?: Framework | string,
  ) {
    if (typeof contentOrFramework === 'object') {
      contentOrFramework = contentOrFramework.startCommand('npm');
    }

    super(artifactId, messageId, id, contentOrFramework);
  }

  run() {
    return this.execute();
  }
}

export class ShellAction extends BaseShellAction {
  readonly type = 'shell';

  run() {
    return this.execute();
  }
}

export class ImportTemplateAction extends BaseAction {
  readonly type = 'import-template';

  constructor(artifactId: string, messageId: string, id: string) {
    super(artifactId, messageId, id);
  }

  async run() {
    /**
     * The import is handled in the template store so this becomes a noop.
     * We use this action to provide more visual feedback as part of the
     * first artifact.
     */
  }
}

export class DeployAction extends BaseAction {
  readonly type = 'deploy';

  deploymentInfo?: GetOrCreateSiteResponse;

  constructor(
    artifactId: string,
    messageId: string,
    id: string,
    readonly provider: string,
  ) {
    super(artifactId, messageId, id);

    // if the llm generates the deploy action, it will use `bolt-hosting`
    this.actionDetails.set(provider === 'bolt-hosting' ? 'Bolt Hosting' : provider);
  }

  showDeploymentURL() {
    if (this.deploymentInfo?.site_url) {
      this.content.set(this.deploymentInfo.site_url);
    }
  }

  async run() {
    try {
      const outputDirectory = this.input as string;

      if (typeof outputDirectory !== 'string') {
        throw new Error('Failed to publish because the build directory was not specified.');
      }

      const projectId = projectStore.getProjectId();

      if (!projectId) {
        // this should not happen, but let's make sure
        throw new Error('Could not publish as we are currently not inside a project.');
      }

      invariant(this.deploymentInfo, 'Deployment info is not set.');

      const instance = await webcontainerPromise;

      const framework = workbenchStore.framework.get();
      const redirectsPath = nodePath.join(outputDirectory, '_redirects');
      const redirectsFile = workbenchStore.files.get()[addWorkDir(redirectsPath)];
      const netlifyTomlFile = workbenchStore.files.get()[addWorkDir(NETLIFY_TOML)];

      if (!redirectsFile && !netlifyTomlFile && framework?.redirects) {
        await instance.fs.writeFile(redirectsPath, framework.redirects);
      }

      const buffer = await instance.internal.serialize(outputDirectory, {
        format: 'zip',
        ignoreSymlinks: true,
        useGitignore: false,
        noDefaultDir: false,
      });

      await deployOnNetlify(projectId, buffer);

      this.showDeploymentURL();

      return this.deploymentInfo;
    } catch (error: any) {
      if (error instanceof Error) {
        error = ChatError.parse(ChatError, error.message, 'An unknown error occurred when publishing your project.');
      }

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SiteDeployFailure,
          properties: {
            provider: this.provider,
            error: error.message,
          },
        },
      });

      throw error;
    }
  }
}

export class BuildAction extends BaseShellAction {
  readonly type = 'build';

  deploymentInfo?: GetOrCreateSiteResponse;

  #buildConfig?: BuildConfig;

  constructor(artifactId: string, messageId: string, id: string) {
    super(artifactId, messageId, id);

    this.shellId = 'deploy';
  }

  setBuildConfig(buildConfig: BuildConfig) {
    this.#buildConfig = buildConfig;
  }

  async run() {
    try {
      const buildConfig = await this.#getBuildConfig();

      // set the content so that the command is visible to the user
      this.content.set(buildConfig.command);

      // check if we have any environment variable that needs to be set for the build command
      const envVariables = this.#getEnvVariables(buildConfig);

      if (envVariables) {
        this.command = `${envVariables} ${this.command}`;
      }

      const exitCode = await super.execute();

      if (exitCode == null) {
        // if exit code is `null`, it means the process was skipped
        return undefined;
      }

      if (exitCode === 130) {
        throw new Error(`Build cancelled. If this happened by mistake, please try again.`);
      } else if (exitCode !== 0) {
        throw new Error(`Failed building the project. Check if there are any build errors and try again.`);
      }

      return buildConfig.output;
    } catch (error: any) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SiteBuildFailed,
          properties: {
            error: error.message,
          },
        },
      });

      throw error;
    }
  }

  #getEnvVariables(buildConfig: BuildConfig) {
    if (!buildConfig.env) {
      return undefined;
    }

    const data: Record<string, string | undefined> = {
      BOLT_DEPLOY_URL: this.deploymentInfo?.site_url,
    };

    return Object.entries(buildConfig.env)
      .map(([key, value]) => {
        return data[value] ? `${key}=${data[value]}` : undefined;
      })
      .filter(Boolean)
      .join(' ');
  }

  async #getBuildConfig() {
    const instance = await webcontainerPromise;

    // if we have a build config defined, it came from the AI and we use that as a last resort
    const buildConfig = (await resolveBuildConfig(instance)) ?? this.#buildConfig;

    if (buildConfig) {
      return buildConfig;
    }

    // TODO link to documentation on how to add this through `.bolt/config.json`
    throw Error(`Could not build the project because no build configuration was found.`);
  }
}

export class BuildAndDeployAction extends PipeAction {
  readonly type = 'build-and-deploy';

  deploymentInfo?: GetOrCreateSiteResponse;

  #buildConfig: BuildConfig | undefined;
  #buildAction: BuildAction;
  #deployAction: DeployAction;

  constructor(artifactId: string, messageId: string, id: string, provider: string) {
    const buildAction = new BuildAction(artifactId, messageId, `${id}-build`);
    const deployAction = new DeployAction(artifactId, messageId, `${id}-deploy`, provider);

    super(artifactId, messageId, id, [buildAction, deployAction]);

    this.#buildAction = buildAction;
    this.#deployAction = deployAction;
  }

  setBuildConfig(buildConfig: BuildConfig) {
    this.#buildConfig = buildConfig;
    this.#buildAction.setBuildConfig(buildConfig);
  }

  async run() {
    const useBuildEndpoint = !!workbenchStore.files.get()[addWorkDir(NETLIFY_TOML)];
    const projectId = projectStore.getProjectId();
    const projectTitle = descriptionStore.get();
    const result = await this.#run(useBuildEndpoint);

    if (this.deploymentInfo) {
      this.deploymentInfo.updated_at = new Date().toISOString();

      importStore.setKey('deploymentInfo', this.deploymentInfo);
    }

    if (result != null) {
      // we only want to send a success event if the deploy was not skipped
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SiteDeploySuccess,
          properties: {
            useBuildEndpoint,
            provider: this.#deployAction.provider,
            ...(projectId && { boltProjectId: projectId }),
            ...(projectTitle && { projectTitle }),
            ...(this.deploymentInfo?.site_url && { siteUrl: this.deploymentInfo.site_url }),
          },
        },
      });
    }

    return result;
  }

  async #buildAndDeployOnNetlify(projectId: string) {
    const instance = await webcontainerPromise;

    // check that the netlify toml includes a build command:

    const netlifyTomlFile = workbenchStore.files.get()[addWorkDir(NETLIFY_TOML)];

    invariant(netlifyTomlFile?.type === 'file');

    // if this fails to parse we throw because we cannot deploy using the build endpoint
    const netlifyToml = parseToml(netlifyTomlFile.content);

    const hasValidBuildCommand =
      netlifyToml &&
      typeof netlifyToml === 'object' &&
      'build' in netlifyToml &&
      netlifyToml.build &&
      typeof netlifyToml.build === 'object' &&
      'command' in netlifyToml.build;

    if (!hasValidBuildCommand) {
      const newNetlifyToml: any = netlifyToml && typeof netlifyToml === 'object' ? netlifyToml : {};

      const existingBuild =
        newNetlifyToml.build && typeof newNetlifyToml.build === 'object' ? newNetlifyToml.build : {};

      const buildConfig = await this.#getBuildConfig();

      newNetlifyToml.build = {
        ...existingBuild,
        command: buildConfig.command,
        publish: buildConfig.output,
      };

      await instance.fs.writeFile(NETLIFY_TOML, stringifyToml(newNetlifyToml));
    }

    const buffer = await instance.internal.serialize(WORK_DIR, {
      format: 'zip',
      ignoreSymlinks: true,
      useGitignore: true,
      noDefaultDir: true,
    });

    const buildInfo = await buildOnNetlify(projectId, buffer);

    const writeSession = workbenchStore.terminalDriver.createWriteSession({ useBufferedOutput: true });

    if (!writeSession) {
      logger.warn('Could not get a write session on the terminal, skipping logs');
      return;
    }

    const cleanupWriteSession = () => {
      writeSession.writeShell(CTRL_C);
      writeSession.release();
    };

    await writeSession.killExistingProcesses();

    writeSession.write(`netlify deploy\n`);

    // we won't have access to the logs (service is down?)
    if (!buildInfo.access_token) {
      this.#buildAction.status.set('complete');
      this.#deployAction.status.set('complete');

      writeSession.write(`${coloredText.red('✖')} Cannot stream logs.`);

      cleanupWriteSession();

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SiteNetlifyLogsFailure,
          properties: {
            error: 'Failed to get an access token to read the logs',
          },
        },
      });

      return;
    }

    const stopSpinner = createSpinner(writeSession, 'Initializing build server...');

    this.#buildAction.status.set('running');

    try {
      await watchBuildLogsFromNetlify(buildInfo, {
        onLog: ({ message, section }, close) => {
          if (
            (section === 'building' && message === 'Section completed: building') ||
            (section === 'cleanup' && message === 'Caching artifacts')
          ) {
            close();

            this.#deployAction.status.set('complete');
            this.#deployAction.showDeploymentURL();

            return;
          }

          if (section === 'building' && message.includes('Failing build')) {
            this.#buildAction.status.set('failed');
            this.#deployAction.status.set('skipped');

            throw new Error('Build failed, ask Bolt to fix the error or check the output panel.');
          }

          if (section === 'deploying' && this.#buildAction.status.get() !== 'complete') {
            this.#buildAction.status.set('complete');
            this.#deployAction.status.set('running');
          }

          if (section === 'building' || section === 'deploying' || section === 'cleanup') {
            stopSpinner();
            writeSession.write(message + '\r\n');
          }
        },
      });
    } finally {
      stopSpinner();
      cleanupWriteSession();
    }
  }

  async #run(useBuildEndpoint: boolean) {
    const projectId = projectStore.getProjectId();
    const projectTitle = descriptionStore.get();
    const hostingProvider = projectStore.integrations.get()?.hosting?.data?.hostingProvider || 'bolt';

    try {
      if (!projectId) {
        // this should not happen, but let's make sure
        throw new Error('Could not deploy as we are currently not inside a project.');
      }

      this.deploymentInfo = await getOrCreateSite(projectId, projectTitle, hostingProvider);

      importStore.setKey('deploymentInfo', this.deploymentInfo);

      this.#buildAction.deploymentInfo = this.deploymentInfo;
      this.#deployAction.deploymentInfo = this.deploymentInfo;
    } catch (error: any) {
      // abort the actions if we failed to create a deploy site
      this.#buildAction.abort();
      this.#deployAction.abort();

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SiteDeployFailure,
          properties: {
            provider: this.#deployAction.provider,
            error: error.message,
            ...(projectId && { boltProjectId: projectId }),
            ...(projectTitle && { projectTitle }),
          },
        },
      });

      throw error;
    }

    if (useBuildEndpoint) {
      try {
        await this.#buildAndDeployOnNetlify(projectId);

        return this.deploymentInfo;
      } catch (error: unknown) {
        let message;

        if (error instanceof TomlError) {
          message = error.message.replace('TOML document', '`netlify.toml`');

          error.message = `${message.slice(0, message.indexOf('\n'))}<pre>${error.codeblock}</pre>`;
        } else if (error instanceof Error) {
          message = error.message;

          const isJSON = isParsableJson(message);

          // we can receive other errors here like Build fail and we do not want to default to unknown error
          if (isJSON) {
            error = ChatError.parse(
              ChatError,
              error.message,
              'An unknown error occurred when publishing your project.',
            );
          }
        } else {
          message = `${error}`;
        }

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.SiteDeployFailure,
            properties: {
              provider: this.#deployAction.provider,
              useBuildingEndpoint: true,
              error: message,
            },
          },
        });

        throw error;
      }
    }

    return super.run();
  }

  async #getBuildConfig() {
    const instance = await webcontainerPromise;

    // if we have a build config defined, it came from the AI and we use that as a last resort
    const buildConfig = (await resolveBuildConfig(instance)) ?? this.#buildConfig;

    if (buildConfig) {
      return buildConfig;
    }

    // TODO link to documentation on how to add this through `.bolt/config.json`
    throw Error(`Could not build the project because no build configuration was found.`);
  }
}

export type BoltAction =
  | FileAction
  | ShellAction
  | ImportTemplateAction
  | ImportProjectAction
  | ImportAction
  | DeployAction
  | BuildAction
  | BuildAndDeployAction
  | InstallAction
  | StartAction
  | FetchFigmaFrameAction
  | GenerateFigmaCodeAction
  | UploadFigmaAssetsAction
  | GithubSyncAction
  | GithubCloneAction
  | SupabaseAction;
